import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Sparkles, BookOpen, ShieldCheck, Clock4, BadgeCheck } from "lucide-react";
import { useCheckoutMode } from "../lib/settings";
import { api } from "../lib/api";
import { scrollToSection } from "../lib/scrollToSection";

// Temporarily hidden until real post-launch satisfaction metrics are collected.
// Flip back to `true` once we have authentic NPS/CSAT data from finished cohorts.
const SHOW_SOCIAL_PROOF_BADGE = false;

// M910 — Hero CTAs are explicit engagement signals; consume one popup-firing
// quota when the user clicks them. The helper sets a one-shot sessionStorage
// flag `suppress_form_popup` that `ExitIntent.fireOnce` reads and clears.
const scrollTo = (id) => scrollToSection(id);

export default function Hero() {
  const { isOnline } = useCheckoutMode();
  const navigate = useNavigate();
  const ctaLabel = isOnline ? "Înscrie-te la curs" : "Înscrie-te";

  // Show the webinar CTA only when there is at least one published, upcoming
  // webinar — same source as the homepage "NextWebinar" section. Silent no-op
  // on error so the hero never breaks.
  const [hasWebinars, setHasWebinars] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.get("/webinars/public")
      .then((r) => { if (!cancelled) setHasWebinars(((r.data?.webinars) || []).length > 0); })
      .catch(() => { /* no webinars surfaced */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 grain-bg opacity-50 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-14 md:pt-20 pb-20 md:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            {/* Accreditation leads the hero: it is the first thing a teacher
                checks before anything else on the page, so it gets its own
                line rather than competing inside the supporting badge row. */}
            <div className="mb-3">
              <span
                data-testid="hero-accreditation"
                className="inline-flex items-center gap-2 rounded-full bg-[#965EA7] px-4 py-2 text-white shadow-[0_4px_16px_rgba(150,94,167,0.32)]"
              >
                <BadgeCheck size={17} strokeWidth={2.25} className="shrink-0" />
                <span className="text-[13px] font-semibold tracking-[0.01em]">
                  Program acreditat
                  <span className="hidden sm:inline"> · OMEC nr. 4712/19.08.2026</span>
                </span>
              </span>
              {/* Narrow screens move the ordinance number below the pill. Both
                  halves use `hidden`/`sm:hidden` (display:none), so a screen
                  reader encounters the number exactly once at any width. */}
              <div className="mt-1.5 text-xs text-[#6B7280] sm:hidden">
                OMEC nr. 4712/19.08.2026
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {[
                { icon: <BookOpen size={14} />, label: "Program de formare continuă" },
                { icon: <Sparkles size={14} />, label: "Eligibil prin Prima de carieră didactică" },
              ].map((b, i) => (
                <span
                  key={i}
                  data-testid={`hero-badge-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#E5E7EB] px-3 py-1 text-xs font-medium text-[#373A45]"
                >
                  <span className="text-[#965EA7]">{b.icon}</span>
                  {b.label}
                </span>
              ))}
            </div>

            <h1
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#373A45] !leading-[1.4] md:!leading-[1.3] lg:!leading-[1.25] tracking-[-0.005em]"
              data-testid="hero-headline"
            >
              Folosește{" "}
              <span className="text-[#7A4986]">Inteligența Artificială</span>{" "}
              pentru a pregăti mai rapid lecții, materiale și activități adaptate elevilor tăi.
            </h1>

            <p className="mt-6 text-lg md:text-xl text-[#373A45]/80 max-w-2xl leading-relaxed">
              Un program practic dedicat cadrelor didactice care vor să economisească timp,
              să creeze materiale mai eficiente și să folosească AI într-un mod util
              și aplicabil la clasă.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                onClick={() => scrollTo("pricing")}
                className="rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white text-base font-semibold px-7 py-6 shadow-[0_4px_14px_0_rgba(150,94,167,0.39)] hover:shadow-[0_8px_24px_rgba(150,94,167,0.35)] transition-all hover:-translate-y-0.5"
                data-testid="hero-enroll-button"
              >
                {ctaLabel}
              </Button>
              <Button
                variant="outline"
                onClick={() => scrollTo("curriculum")}
                className="rounded-full border-2 border-[#373A45] text-[#373A45] hover:bg-[#373A45] hover:text-white text-base font-semibold px-7 py-6"
                data-testid="hero-curriculum-button"
              >
                Vezi ce vei învăța
              </Button>
              {hasWebinars && (
                <Button
                  variant="outline"
                  onClick={() => navigate("/webinarii")}
                  className="rounded-full border-2 border-[#965EA7] text-[#965EA7] hover:bg-[#965EA7] hover:text-white text-base font-semibold px-7 py-6"
                  data-testid="hero-webinars-button"
                >
                  <Sparkles size={16} className="mr-1.5" /> Înscrie-te gratuit la un webinar
                </Button>
              )}
            </div>

            <p
              className="mt-4 text-sm text-[#6B7280] max-w-2xl leading-relaxed"
              data-testid="hero-flexibility-note"
            >
              Programăm sesiunile live ținând cont de disponibilitatea participanților
              și de perioadele aglomerate din calendarul școlar.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-[#6B7280]">
              <div className="flex items-center gap-2">
                <Clock4 size={16} className="text-[#3F8EBA]" /> 25 de ore de formare
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} className="text-[#519872]" /> Certificat de participare
              </div>
              <div className="flex items-center gap-2">
                <BookOpen size={16} className="text-[#965EA7]" /> 10 module aplicate pentru profesori
              </div>
            </div>
          </motion.div>

          {/* Right – image with floating card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-3xl overflow-hidden border border-[#E5E7EB] shadow-xl">
              <img
                src="https://images.pexels.com/photos/5212703/pexels-photo-5212703.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Profesor folosind AI în clasă"
                className="w-full h-[460px] md:h-[540px] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#373A45]/30 to-transparent" />
            </div>

            <div className="absolute -bottom-6 -left-6 hidden md:block animate-float-slow">
              <div className="glass rounded-2xl p-5 w-72 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-[#3F8EBA] grid place-items-center text-white shrink-0">
                    <Sparkles size={18} />
                  </div>
                  <div className="font-display text-base font-semibold text-[#373A45] leading-snug">
                    Economisește timp la pregătirea lecțiilor
                  </div>
                </div>
              </div>
            </div>

            {SHOW_SOCIAL_PROOF_BADGE && (
              <div className="absolute -top-4 -right-4 hidden md:block">
                <div className="bg-[#373A45] text-white rounded-full px-5 py-2 text-sm font-semibold shadow-lg">
                  ★ 4.9 / 5 satisfacție
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
